import { useState } from "react";
import { PlayCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useIslands } from "../../lib/useIslands";
import { Field } from "./Field";

export const SourcesTab = ({ sourcesByIsland, toggleSource, reload }) => {
  const [adding, setAdding] = useState(null);
  const [form, setForm] = useState({ name: "", url: "", rss: "", color: "#0AADA0" });
  const [saving, setSaving] = useState(false);
  const [testingName, setTestingName] = useState("");
  const { islands } = useIslands();

  const openAdd = (island) => {
    setForm({ name: "", url: "", rss: "", color: "#0AADA0" });
    setAdding(island);
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Source name required");
    setSaving(true);
    try {
      await api.post("/admin/sources/new", { ...form, island: adding, is_active: true });
      toast.success("Source added");
      setAdding(null);
      reload && reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally { setSaving(false); }
  };
  const del = async (name) => {
    if (!window.confirm(`Delete source "${name}"?`)) return;
    try {
      await api.delete(`/admin/sources/${encodeURIComponent(name)}`);
      toast.success("Source deleted");
      reload && reload();
    } catch { toast.error("Delete failed"); }
  };
  const testScrape = async (name) => {
    setTestingName(name);
    try {
      await api.post(`/admin/sources/${encodeURIComponent(name)}/scrape`);
      toast.success(`Test scrape started for ${name} — check the Overview tab in 30s`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test scrape failed");
    } finally {
      setTimeout(() => setTestingName(""), 1500);
    }
  };

  const managedIslands = islands.filter((i) => i.slug !== "all");

  return (
    <div className="space-y-6" data-testid="admin-sources-view">
      {managedIslands.map((i) => (
        <div key={i.slug} className="card-soft p-5" data-testid={`admin-sources-${i.slug}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm uppercase tracking-wider text-[#1A1A1A] flex items-center gap-2">
              <span>{i.flag}</span>
              <span>{i.name}</span>
              <span className="text-xs text-[#6B7280] font-normal">
                {(sourcesByIsland[i.slug] || []).length} source(s)
              </span>
            </h3>
            <button
              onClick={() => openAdd(i.slug)}
              className="text-xs px-3 py-1.5 rounded-full bg-[#0AADA0]/10 text-[#0AADA0] hover:bg-[#0AADA0]/20"
              data-testid={`add-source-${i.slug}`}
            >
              + Add Source
            </button>
          </div>
          <ul className="divide-y divide-[#E5E5E5]">
            {(sourcesByIsland[i.slug] || []).map((s) => {
              const nameId = s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <li key={s.name} className="py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[#1A1A1A] truncate">{s.name}</div>
                      <a href={s.url} target="_blank" rel="noopener noreferrer"
                         className="text-xs text-[#6B7280] hover:text-[#0AADA0] truncate block">
                        {s.url}
                      </a>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleSource(s.name)}
                    data-testid={`admin-source-toggle-${nameId}`}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                      s.enabled
                        ? "bg-[#0AADA0]/10 text-[#0AADA0] hover:bg-[#0AADA0]/20"
                        : "bg-[#E63946]/10 text-[#E63946] hover:bg-[#E63946]/20"
                    }`}>
                    {s.enabled ? "Enabled" : "Disabled"}
                  </button>
                  <button
                    onClick={() => testScrape(s.name)}
                    disabled={testingName === s.name}
                    className="p-1.5 rounded text-[#6B7280] hover:bg-[#0AADA0]/10 hover:text-[#0AADA0] disabled:opacity-50"
                    data-testid={`admin-source-test-${nameId}`}
                    aria-label="Test scrape"
                    title="Test scrape"
                  >{testingName === s.name ? "…" : <PlayCircle size={14} />}</button>
                  <button
                    onClick={() => del(s.name)}
                    className="p-1.5 rounded text-[#6B7280] hover:bg-[#E63946]/10 hover:text-[#E63946]"
                    data-testid={`admin-source-delete-${nameId}`}
                    aria-label="Delete"
                  ><Trash2 size={14} /></button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {adding && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAdding(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="add-source-modal">
            <h3 className="font-medium text-[#1A1A1A]">
              Add source to {adding}
            </h3>
            <Field label="Source name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="source-input-name" placeholder="e.g. Barbados Today" />
            <Field label="Website URL" value={form.url} onChange={(v) => setForm({ ...form, url: v })} testid="source-input-url" placeholder="https://example.com" />
            <Field label="RSS feed URL (recommended)" value={form.rss} onChange={(v) => setForm({ ...form, rss: v })} testid="source-input-rss" placeholder="https://example.com/feed/" />
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">Accent color</label>
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                     className="h-9 w-16 rounded border border-[#E5E5E5]"
                     data-testid="source-input-color" />
            </div>
            <div className="flex items-center gap-2 justify-end pt-2">
              <button onClick={() => setAdding(null)} className="px-4 py-2 text-sm text-[#6B7280] hover:bg-[#FAFAFA] rounded-full" data-testid="source-cancel-btn">
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                      className="px-4 py-2 rounded-full bg-[#0AADA0] text-white text-sm font-medium hover:bg-[#0AADA0]/90 disabled:opacity-50"
                      data-testid="source-save-btn">
                {saving ? "Saving…" : "Add Source"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
